import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterArticle } from '../models/helpCenterArticle';
import { getHelpCenterArticle } from '../services/helpCenterService';
import { HelpArticleBody } from './helpArticleBody';

type HelpPanelArticleProps = {
  slug: string;
  onBack: () => void;
  /** Länken till hjälpcentret navigerar bort; panelen stängs då. */
  onNavigate: () => void;
  /** Titeln, eller feltexten, får fokus när den visas. */
  autoFocus: boolean;
};

/** En artikel i hjälppanelen: samma innehåll som artikelsidan, men utan brödsmulor och kategori. */
export const HelpPanelArticle = (props: HelpPanelArticleProps) => {
  const { slug, onBack, onNavigate, autoFocus } = props;
  const { t } = useTranslation('help');

  const [article, setArticle] = useState<HelpCenterArticle | null>(null);
  const [error, setError] = useState<'notFound' | 'failed' | null>(null);

  // Titeln eller feltexten får fokus när den visas, så att skärmläsare och tangentbord hamnar i artikeln.
  const focusOnMount = useCallback((element: HTMLElement | null) => element?.focus({ preventScroll: true }), []);
  const focusRef = autoFocus ? focusOnMount : undefined;

  // En avpublicerad eller arkiverad artikel svarar 404 utan toast. cancelled: ett sent svar för en artikel man redan
  // lämnat skriver inte över den nya.
  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setError(null);
    const load = async () => {
      try {
        const result = await getHelpCenterArticle(slug);
        if (!cancelled) setArticle(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) setError((e as { status?: number })?.status === 404 ? 'notFound' : 'failed');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Button size='small' startIcon={<ArrowBack fontSize='small' />} onClick={onBack} sx={{ ml: -1 }}>
          {t('help:panelBack')}
        </Button>
        {/* Text istället för en ikon: en ikon för fullvy ser ut att förstora panelen, inte att byta sida. */}
        {!error && (
          <MuiLink
            component={Link}
            to={`${RoutesEnum.HelpArticle}/${encodeURIComponent(slug)}`}
            onClick={onNavigate}
            underline='hover'
            variant='body2'
          >
            {t('help:panelOpenHelpCenter')}
          </MuiLink>
        )}
      </Box>
      {!article && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Typography ref={focusRef} tabIndex={-1} color='text.secondary' sx={{ outline: 'none' }}>
          {t(error === 'failed' ? 'help:loadFailed' : 'help:articleNotFound')}
        </Typography>
      )}
      {article && (
        <>
          <Typography ref={focusRef} tabIndex={-1} variant='h6' component='h3' sx={{ fontWeight: 700, outline: 'none' }}>
            {article.title}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {t('help:updatedAt', { date: DateHelper.getDate(article.updatedAt) })}
          </Typography>
          <HelpArticleBody bodyHtml={article.bodyHtml} />
        </>
      )}
    </>
  );
};
